import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SlidersHorizontal } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { ProductCard } from "../components/ProductCard";
import { SEO } from "../components/SEO";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { getCategory } from "../lib/api";

export default function CategoryPage() {
  const { slug } = useParams();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [sort, setSort] = useState("featured");

  const activeSub = sp.get("sub") || "all";
  const search = sp.get("search") || "";

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setNotFound(false);
    getCategory(slug).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  const setSub = (s) => {
    const next = new URLSearchParams(sp);
    if (s === "all") next.delete("sub"); else next.set("sub", s);
    setSp(next);
  };

  const products = useMemo(() => {
    if (!data) return [];
    let items = [...data.products];
    if (activeSub !== "all") items = items.filter((p) => p.subcategory === activeSub);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
    }
    if (sort === "name") items.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "sku") items.sort((a, b) => a.sku.localeCompare(b.sku));
    else items.sort((a, b) => (b.featured - a.featured) || (b.bestseller - a.bestseller) || a.name.localeCompare(b.name));
    return items;
  }, [data, activeSub, search, sort]);

  if (notFound) return <div data-testid="category-notfound" className="min-h-screen grid place-items-center pt-40"><div className="text-center"><h1 className="font-display font-bold text-4xl text-vr-ink mb-4">Category not found</h1><Link to="/products" className="text-vr-brand font-semibold underline">All categories</Link></div></div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-vr-dim pt-40">Loading…</div>;

  const { category, subcategories } = data;

  return (
    <main data-testid="category-page" className="pt-[104px]">
      <SEO
        title={`Buy ${category.name} Wholesale | VR Packaging`}
        description={`${category.name} for New Zealand businesses. ${(category.description || '').replace(/\s+/g,' ').trim()} Trade pricing, nationwide dispatch — request a quote.`}
        path={`/category/${category.slug}`}
      />
      <div className="bg-vr-navy text-white">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
          <Breadcrumbs items={[{ label: "Products", to: "/products" }, { label: category.name }]} />
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl md:text-6xl">{category.name}</h1>
          <p className="mt-4 text-white/70 max-w-3xl">{category.description}</p>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        {/* Subcategory filters */}
        {subcategories.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm font-semibold text-vr-ink mb-4"><SlidersHorizontal size={16} /> Filter by type</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSub("all")} data-testid="subfilter-all" className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${activeSub === "all" ? "bg-vr-brand text-white border-vr-brand" : "bg-white text-vr-dim border-vr-border hover:border-vr-brand"}`}>All</button>
              {subcategories.map((s) => (
                <button key={s.slug} onClick={() => setSub(s.slug)} data-testid={`subfilter-${s.slug}`} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${activeSub === s.slug ? "bg-vr-brand text-white border-vr-brand" : "bg-white text-vr-dim border-vr-border hover:border-vr-brand"}`}>{s.name}</button>
              ))}
            </div>
          </div>
        )}

        {/* sort + count */}
        <div className="flex items-center justify-between gap-4 mb-8 pb-5 border-b border-vr-border">
          <span data-testid="product-count" className="text-sm text-vr-dim">{products.length} product{products.length !== 1 ? "s" : ""}{search && <> for “<span className="text-vr-ink font-medium">{search}</span>”</>}</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-vr-dim hidden sm:inline">Sort by</span>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-[180px] bg-white border-vr-border"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="name">Name (A–Z)</SelectItem>
                <SelectItem value="sku">Product code</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {products.length === 0 ? (
          <div className="py-24 text-center text-vr-dim">No products found. Try a different filter.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {products.map((p, i) => <ProductCard key={p.sku} p={p} index={i} />)}
          </div>
        )}
      </div>
    </main>
  );
}
